
import * as React from "react"

const MOBILE_BREAKPOINT = 768

export type MobileInfo = {
  isMobile: boolean;
  isIOSDevice: boolean;
  isChromeOnAndroid: boolean;
  isSafari: boolean;
  isStandalone: boolean;
}

export function useIsMobile(): boolean & MobileInfo {
  const [isMobile, setIsMobile] = React.useState<boolean>(false)
  const [isIOSDevice, setIsIOSDevice] = React.useState<boolean>(false)
  const [isChromeOnAndroid, setIsChromeOnAndroid] = React.useState<boolean>(false)
  const [isSafari, setIsSafari] = React.useState<boolean>(false)
  const [isStandalone, setIsStandalone] = React.useState<boolean>(false)
  
  React.useEffect(() => {
    // Detect if device is mobile via screen size
    const checkMobileSize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    }
    
    // Detect iOS specifically
    const checkIOSDevice = () => {
      const userAgent = window.navigator.userAgent.toLowerCase()
      setIsIOSDevice(
        /iphone|ipad|ipod/.test(userAgent) || 
        (userAgent.includes('mac') && 'ontouchend' in document)
      )
    }
    
    // Detect Chrome on Android specifically
    const checkChromeOnAndroid = () => {
      const userAgent = window.navigator.userAgent.toLowerCase()
      setIsChromeOnAndroid(/android/i.test(userAgent) && /chrome/i.test(userAgent))
    }
    
    // Detect Safari browser
    const checkSafari = () => {
      const userAgent = window.navigator.userAgent.toLowerCase()
      setIsSafari(/safari/i.test(userAgent) && !/chrome/i.test(userAgent))
    }
    
    // Check if the app is running in standalone mode (PWA)
    const checkStandalone = () => {
      setIsStandalone(
        window.matchMedia('(display-mode: standalone)').matches || 
        (window.navigator as any).standalone === true
      )
    }
    
    // Initial checks - run in a small timeout to ensure DOM is fully loaded
    setTimeout(() => {
      try {
        checkMobileSize()
        checkIOSDevice()
        checkChromeOnAndroid()
        checkSafari()
        checkStandalone()
      } catch (error) {
        console.error("Error during mobile detection:", error)
        // Fallback to basic detection if errors occur
        setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
      }
    }, 10)
    
    // Set up event listeners
    window.addEventListener('resize', checkMobileSize)
    
    // Clean up
    return () => {
      window.removeEventListener('resize', checkMobileSize)
    }
  }, [])

  // For compatibility with components that expect just a boolean
  const mobileCheck = isMobile as boolean & MobileInfo

  // Return both the boolean for backward compatibility and the detailed object
  return Object.assign(mobileCheck, {
    isMobile,
    isIOSDevice,
    isChromeOnAndroid,
    isSafari,
    isStandalone
  })
}
